import { useAuthStore } from '../hooks/useStore';
import { joinApiUrl } from './url';
import type { AuthOwnerSnapshot } from '@/utils/authOwner';

export const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');
export const apiUrl = (endpoint: string) => joinApiUrl(API_BASE_URL, endpoint);

/** Structured API error with HTTP status and server message */
export class ApiError extends Error {
  status: number;
  data: Record<string, unknown> | null;

  constructor(message: string, status: number, data: Record<string, unknown> | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | undefined>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export class ApiClient {
  private baseUrl: string;
  private refreshOperations = new Map<string, { promise: Promise<string | null> }>();
  private tokenRotations = new Map<string, { nextToken: string; userId: string }>();

  private static readonly MAX_TOKEN_ROTATIONS = 32;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get token from Zustand auth store
   */
  getToken(): string | null {
    return useAuthStore.getState().token;
  }

  /**
   * Remember a bounded token lineage so a slow 401 from the previous bearer can
   * reuse a refresh that already completed for the same account.
   */
  private rememberTokenRotation(previousToken: string, nextToken: string, userId: string): void {
    this.tokenRotations.delete(previousToken);
    this.tokenRotations.set(previousToken, { nextToken, userId });

    while (this.tokenRotations.size > ApiClient.MAX_TOKEN_ROTATIONS) {
      const oldestToken = this.tokenRotations.keys().next().value as string | undefined;
      if (!oldestToken) break;
      this.tokenRotations.delete(oldestToken);
    }
  }

  private isKnownTokenRotation(
    previousToken: string,
    currentToken: string,
    userId: string,
  ): boolean {
    const visited = new Set<string>();
    let candidate = previousToken;

    while (visited.size < ApiClient.MAX_TOKEN_ROTATIONS && !visited.has(candidate)) {
      visited.add(candidate);
      const rotation = this.tokenRotations.get(candidate);
      if (!rotation || rotation.userId !== userId) return false;
      if (rotation.nextToken === currentToken) return true;
      candidate = rotation.nextToken;
    }

    return false;
  }

  /** Accept only the captured sign-in, including refreshes performed by this client. */
  ownsCurrentSession(owner: AuthOwnerSnapshot): boolean {
    const current = useAuthStore.getState();
    return Boolean(
      owner.isAuthenticated &&
      current.isAuthenticated &&
      owner.userId &&
      owner.userId === current.user?.id &&
      owner.token &&
      current.token &&
      (owner.token === current.token ||
        this.isKnownTokenRotation(owner.token, current.token, owner.userId)),
    );
  }

  /**
   * Attempt to refresh the auth token. Deduplicates concurrent refresh calls
   * so multiple 401s don't trigger multiple refresh requests.
   */
  private async tryRefreshToken(expectedToken: string): Promise<string | null> {
    const existingOperation = this.refreshOperations.get(expectedToken);
    if (existingOperation) return existingOperation.promise;

    const initialStore = useAuthStore.getState();
    const expectedUser = initialStore.user;
    if (initialStore.token !== expectedToken || !expectedUser) return null;

    const refresh = async (): Promise<string | null> => {
      try {
        const response = await fetch(joinApiUrl(this.baseUrl, '/auth/refresh'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${expectedToken}`,
          },
        });

        if (!response.ok) return null;

        const data = (await response.json()) as { token?: string };
        if (data.token) {
          const store = useAuthStore.getState();
          // A refresh response belongs only to the exact auth owner that
          // initiated it. Never attach an old account's refreshed bearer to a
          // user who signed in while the network request was pending.
          if (store.token !== expectedToken || store.user?.id !== expectedUser.id) return null;

          this.rememberTokenRotation(expectedToken, data.token, expectedUser.id);
          // Preserve same-owner profile changes that may have landed while the
          // refresh request was in flight.
          store.setAuth(store.user, data.token);
          return data.token;
        }
        return null;
      } catch {
        return null;
      }
    };

    const operation = { promise: Promise.resolve<string | null>(null) };
    operation.promise = refresh().finally(() => {
      if (this.refreshOperations.get(expectedToken) === operation) {
        this.refreshOperations.delete(expectedToken);
      }
    });
    this.refreshOperations.set(expectedToken, operation);
    return operation.promise;
  }

  private clearAuthState() {
    sessionStorage.removeItem('pm-auth-storage');
    sessionStorage.removeItem('hr-auth-storage');
    useAuthStore.getState().clearAuth();
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry = false,
  ): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = joinApiUrl(this.baseUrl, endpoint);

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...fetchOptions.headers,
    };

    // Public auth endpoints that should never send an Authorization header.
    // Sending a stale/blocklisted token on these can cause unexpected errors.
    const isPublicAuthEndpoint =
      endpoint === '/auth/login' ||
      endpoint === '/auth/forgot-password' ||
      endpoint === '/auth/reset-password' ||
      endpoint.startsWith('/auth/verify-reset-token/') ||
      endpoint.startsWith('/auth/verify-invite-token/') ||
      endpoint === '/auth/accept-invite' ||
      endpoint === '/auth/google/status' ||
      endpoint === '/auth/google/exchange';

    const authAtRequest = useAuthStore.getState();
    const token = authAtRequest.token;
    const tokenUserId = authAtRequest.user?.id ?? null;
    const hasExplicitAuthorization = Boolean(
      (headers as Record<string, string>).Authorization ||
      (headers as Record<string, string>).authorization,
    );
    if (token && !isPublicAuthEndpoint && !hasExplicitAuthorization) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (response.status === 401 && !isPublicAuthEndpoint && !hasExplicitAuthorization) {
      // On first 401, attempt token refresh before giving up
      if (!isRetry && token) {
        const currentAuth = useAuthStore.getState();
        const newToken =
          currentAuth.token === token
            ? await this.tryRefreshToken(token)
            : tokenUserId &&
                currentAuth.user?.id === tokenUserId &&
                currentAuth.token &&
                this.isKnownTokenRotation(token, currentAuth.token, tokenUserId)
              ? currentAuth.token
              : null;
        if (newToken && useAuthStore.getState().token === newToken) {
          return this.request<T>(endpoint, options, true);
        }
      }

      // Refresh failed or this is already a retry — clear auth
      const currentToken = this.getToken();
      if (!currentToken || currentToken === token) {
        this.clearAuthState();
      }
      throw new ApiError('Unauthorized', 401);
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      // Response body is empty or not valid JSON — likely a proxy/network issue
      // (e.g. Cloudflare Pages _redirects failing to proxy POST requests)
      const status = response.status;
      if (status === 0 || status >= 502) {
        throw new ApiError('API server is unreachable. Please try again later.', status);
      }
      throw new ApiError(
        `Unexpected server response (${status}). The API may be unavailable.`,
        status,
      );
    }

    if (!response.ok) {
      const errorData = isRecord(data) ? data : null;
      // For 2FA required responses, include the full response data
      if (response.status === 403 && errorData?.requires_2fa === true) {
        const error = new ApiError(
          typeof errorData.error === 'string' ? errorData.error : '2FA verification required',
          403,
          errorData,
        ) as ApiError & { requires_2fa: boolean };
        error.requires_2fa = true;
        throw error;
      }
      throw new ApiError(
        typeof errorData?.error === 'string' ? errorData.error : 'An error occurred',
        response.status,
        errorData,
      );
    }

    return data as T;
  }

  get<T>(
    endpoint: string,
    params?: Record<string, string | number | undefined>,
    options: RequestOptions = {},
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET', params });
  }

  post<T>(endpoint: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  patch<T>(endpoint: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(endpoint: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(endpoint: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

export const api = new ApiClient(API_BASE_URL);
